import { create } from "zustand";

/** Deep-link focus target set from Telegram startapp param before map loads. */
export type DeepLinkFocusTarget =
    | { type: 'plot'; mapObjectId: number }
    /** Town hall — optionally open the voting screen (Telegram election CTAs). */
    | { type: 'cityhall'; openVote?: boolean };

export type RoomPanel = 'shop' | 'inventory';

interface InterfaceStore {
    hidden: boolean;
    setHidden: (hidden: boolean) => void;
    // Room view state
    currentView: 'map' | 'room';
    currentRoomMapObjectId: number | null;
    currentRoomOwnerId: number | null;
    setRoomView: (mapObjectId: number, ownerId: number | null) => void;
    setMapView: () => void;
    // Build mode (furniture editing) — frontend-only, OFF on every entry into a house;
    // only the player turns it on. Kept while the furniture shop/inventory is open
    // (those don't leave the house), reset by setRoomView / setMapView.
    roomBuildMode: boolean;
    setRoomBuildMode: (on: boolean) => void;
    // Furniture shop / inventory, opened over the room. A panel, not a route: a
    // route unmounted the room, and every trip to the shop rebuilt the whole scene
    // (and on iPhones piled up enough memory to drop the map's WebGL context).
    roomPanel: RoomPanel | null;
    setRoomPanel: (panel: RoomPanel | null) => void;
    // ID об'єкта, до модалки якого треба повернутись після виходу з інтерʼєру
    returnToMapObjectId: number | null;
    setReturnToMapObjectId: (id: number) => void;
    clearReturnToMapObjectId: () => void;
    // ID щойно купленого будинку — тригерить zoom + підсвічування в MapCanvas
    purchaseHighlightMapObjectId: number | null;
    setPurchaseHighlightMapObjectId: (id: number | null) => void;
    // Catalog ID щойно купленої/розміщеної меблі — тригерить зелену рамку + z-boost в RoomCanvas
    justPlacedFurnitureCatalogId: number | null;
    setJustPlacedFurnitureCatalogId: (id: number | null) => void;
    // Pending deep-link camera focus from Telegram startapp param (plot_<id> or cityhall)
    pendingDeepLinkFocus: DeepLinkFocusTarget | null;
    setPendingDeepLinkFocus: (target: DeepLinkFocusTarget | null) => void;
    clearPendingDeepLinkFocus: () => void;
}

const useInterfaceStore = create<InterfaceStore>((set) => ({
    hidden: false,
    setHidden: (hidden) => set({ hidden }),
    // Room view state
    currentView: 'map',
    currentRoomMapObjectId: null,
    currentRoomOwnerId: null,
    returnToMapObjectId: null,
    roomBuildMode: false,
    setRoomBuildMode: (on) => set({ roomBuildMode: on }),
    roomPanel: null,
    setRoomPanel: (panel) => set({ roomPanel: panel }),
    setRoomView: (mapObjectId, ownerId) => set((state) => ({
        // Coming back to the same house (after a purchase / picking from the
        // inventory) keeps build mode; entering a house from the map resets it.
        roomBuildMode: state.currentView === 'room' && state.currentRoomMapObjectId === mapObjectId ? state.roomBuildMode : false,
        roomPanel: null,
        currentView: 'room',
        currentRoomMapObjectId: mapObjectId,
        currentRoomOwnerId: ownerId,
        returnToMapObjectId: mapObjectId, // Зберігаємо для повернення до модалки
    })),
    setMapView: () => set({
        roomBuildMode: false,
        roomPanel: null,
        currentView: 'map',
        currentRoomMapObjectId: null,
        currentRoomOwnerId: null,
        // returnToMapObjectId НЕ скидаємо тут — MapCanvas прочитає і відкриє модалку
    }),
    setReturnToMapObjectId: (id) => set({ returnToMapObjectId: id }),
    clearReturnToMapObjectId: () => set({ returnToMapObjectId: null }),
    purchaseHighlightMapObjectId: null,
    setPurchaseHighlightMapObjectId: (id) => set({ purchaseHighlightMapObjectId: id }),
    justPlacedFurnitureCatalogId: null,
    setJustPlacedFurnitureCatalogId: (id) => set({ justPlacedFurnitureCatalogId: id }),
    pendingDeepLinkFocus: null,
    setPendingDeepLinkFocus: (target) => set({ pendingDeepLinkFocus: target }),
    clearPendingDeepLinkFocus: () => set({ pendingDeepLinkFocus: null }),
}));

export default useInterfaceStore;
